import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { finalize, Subject, switchMap } from 'rxjs';
import {
  APIError,
  ImageCompressionService,
  UsersAPIService,
} from 'shared/services';
import { AlertService, ToastService, TypedDialog } from 'shared/ui';

@Component({
  selector: 'lib-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrl: './edit-profile.component.scss',
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: 'mobile-full-height' },
})
export class EditProfileComponent
  extends TypedDialog<
    { initial: boolean; profileImage?: string; profileName?: string },
    { canceled: boolean; profileImage?: string; profileName?: string }
  >
  implements OnInit, OnDestroy
{
  @ViewChild('fileInput')
  private _fileInput!: ElementRef<HTMLInputElement>;

  private _isDuplicateSubject = new Subject<string>();
  private _myProfileChangeSubject = new Subject<{
    nickname: string;
    avatar_image_url: string;
  }>();
  private _imageUploadSubject = new Subject<any>();

  public _baseAvatarImage: any;
  public _nickname: string = '';
  public _isTextLength: number = 0;
  public _uploadImage: any;
  public _isPageInit = true;
  public _isDuplication:
    | 'duplicate'
    | 'not-duplicate'
    | 'test-necessary'
    | 'empty' = 'empty';
  public _isImageChanged: boolean = false;
  public _isImageUploading: boolean = false;
  public _showImageUploadingLoader: boolean = false;
  public _isDuplicateChecking: boolean = false;
  public _showDuplicateCheckingLoader: boolean = false;
  public _isProfileSaving: boolean = false;
  public _showProfileSavingLoader: boolean = false;
  public _isSubmitLocked: boolean = false;
  private _duplicateLoaderTimeout: ReturnType<typeof setTimeout> | null = null;
  private _profileSavingLoaderTimeout: ReturnType<typeof setTimeout> | null =
    null;
  private _imageUploadLoaderTimeout: ReturnType<typeof setTimeout> | null =
    null;
  /** 마지막으로 중복 확인 API가 끝난 닉네임(이번 다이얼로그에서만 사용) */
  public _lastDuplicateCheckedNickname: string | null = null;
  /** 마지막 중복 확인 API 응답(같은 닉네임으로 되돌렸을 때 상태 복원용) */
  private _lastDuplicateCheckWasDuplicate: boolean | null = null;
  /** 다이얼로그 오픈 시점의 닉네임(서버에서 넘어온 값) */
  private _profileNicknameAtOpen = '';
  /** 오픈 시 값과 달라진 적이 있으면 true — 막 연 직후에는 중복 확인 불가 */
  public _nicknameEverDeviatedFromOpen = false;
  public get _trimmedNicknameLength(): number {
    return this._nickname.trim().length;
  }

  public get _isSameNicknameAsCurrent(): boolean {
    return this._nickname === this._profileNicknameAtOpen;
  }

  constructor(
    private _alertService: AlertService,
    private _usersAPIService: UsersAPIService,
    private _imageCompressionService: ImageCompressionService,
    private _toastService: ToastService
  ) {
    super();
  }

  ngOnInit(): void {
    this._uploadImage = this._data.profileImage;
    this._baseAvatarImage = this._data.profileImage;
    this._nickname = this._data.profileName || '';
    this._profileNicknameAtOpen = this._nickname;

    this._isDuplicateSubject
      .pipe(
        switchMap((nickname: string) => {
          return this._usersAPIService.checkNicknameDuplication(nickname).pipe(
            finalize(() => {
              this._isDuplicateChecking = false;
              this._clearDuplicateLoaderTimeout();
              this._showDuplicateCheckingLoader = false;
            })
          );
        })
      )
      .subscribe({
        next: (duplicated) => {
          this._isDuplication = duplicated ? 'duplicate' : 'not-duplicate';
          this._lastDuplicateCheckedNickname = this._nickname;
          this._lastDuplicateCheckWasDuplicate = duplicated;
        },
        error: (error: APIError) => {
          this._toastService.openWarning(error.message);
        },
      });

    this._myProfileChangeSubject
      .pipe(
        switchMap(({ nickname, avatar_image_url }) => {
          if (nickname === '') {
            nickname = this._nickname;
          }
          return this._usersAPIService
            .changeMyProfile(nickname, avatar_image_url)
            .pipe(
              finalize(() => {
                this._isProfileSaving = false;
                this._clearProfileSavingLoaderTimeout();
                this._showProfileSavingLoader = false;
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._dialogRef.close({
            canceled: false,
            profileImage: this._uploadImage,
            profileName: this._nickname,
          });
        },
        error: (error: APIError) => {
          this._isSubmitLocked = false;
          this._toastService.openWarning(error.message);
        },
      });

    this._imageUploadSubject
      .pipe(
        switchMap((imageFile) => {
          return this._usersAPIService.uploadImage(imageFile).pipe(
            finalize(() => {
              this._clearImageUploadLoaderTimeout();
              this._showImageUploadingLoader = false;
              this._isImageUploading = false;
            })
          );
        })
      )
      .subscribe({
        next: (result) => {
          this._uploadImage = result[0].url;
          this._isImageChanged = true;
        },
        error: (error: APIError) => {
          this._toastService.openWarning(error.message);
        },
      });
  }

  ngOnDestroy(): void {
    this._clearDuplicateLoaderTimeout();
    this._clearProfileSavingLoaderTimeout();
    this._clearImageUploadLoaderTimeout();
    this._isDuplicateSubject.complete();
    this._myProfileChangeSubject.complete();
    this._imageUploadSubject.complete();
  }

  private _clearDuplicateLoaderTimeout(): void {
    if (this._duplicateLoaderTimeout) {
      clearTimeout(this._duplicateLoaderTimeout);
      this._duplicateLoaderTimeout = null;
    }
  }

  private _clearProfileSavingLoaderTimeout(): void {
    if (this._profileSavingLoaderTimeout) {
      clearTimeout(this._profileSavingLoaderTimeout);
      this._profileSavingLoaderTimeout = null;
    }
  }

  private _clearImageUploadLoaderTimeout(): void {
    if (this._imageUploadLoaderTimeout) {
      clearTimeout(this._imageUploadLoaderTimeout);
      this._imageUploadLoaderTimeout = null;
    }
  }

  public _closeDialog(): void {
    if (this._isProfileSaving) {
      return;
    }

    if (this._data.initial) {
      this._usersAPIService.cancelChangeProfile().subscribe();
    }

    this._dialogRef.close({ canceled: true });
  }

  public _changeNickname(): void {
    if (this._isProfileSaving) {
      return;
    }

    this._isTextLength = this._trimmedNicknameLength;
    this._isPageInit = false;
    if (this._nickname !== this._profileNicknameAtOpen) {
      this._nicknameEverDeviatedFromOpen = true;
    }

    if (this._trimmedNicknameLength === 0) {
      this._isDuplication = 'empty';
    } else if (
      this._lastDuplicateCheckedNickname !== null &&
      this._nickname === this._lastDuplicateCheckedNickname &&
      this._lastDuplicateCheckWasDuplicate !== null
    ) {
      this._isDuplication = this._lastDuplicateCheckWasDuplicate
        ? 'duplicate'
        : 'not-duplicate';
    } else {
      this._isDuplication = 'test-necessary';
    }
  }

  public _onClickDuplicate(): void {
    if (this._isProfileSaving) {
      return;
    }

    if (
      (this._isDuplication === 'not-duplicate' ||
        this._isDuplication === 'duplicate') &&
      this._nickname === this._lastDuplicateCheckedNickname
    ) {
      return;
    }

    if (
      this._lastDuplicateCheckedNickname === null &&
      !this._nicknameEverDeviatedFromOpen
    ) {
      return;
    }

    if (this._isDuplicateChecking) {
      return;
    }

    if (this._trimmedNicknameLength === 0) {
      return;
    }

    if (
      this._trimmedNicknameLength < 2 ||
      this._trimmedNicknameLength > 20 ||
      this._isSameNicknameAsCurrent
    ) {
      return;
    }

    this._isDuplicateChecking = true;
    this._showDuplicateCheckingLoader = false;
    this._clearDuplicateLoaderTimeout();
    this._duplicateLoaderTimeout = setTimeout(() => {
      if (this._isDuplicateChecking) {
        this._showDuplicateCheckingLoader = true;
      }
      this._duplicateLoaderTimeout = null;
    }, 1000);
    this._isDuplicateSubject.next(this._nickname);
  }

  public _onClickSubmit(): void {
    if (this._isProfileSaving || this._isSubmitLocked) {
      return;
    }

    this._isSubmitLocked = true;
    this._isProfileSaving = true;
    this._showProfileSavingLoader = false;
    this._clearProfileSavingLoaderTimeout();
    this._profileSavingLoaderTimeout = setTimeout(() => {
      if (this._isProfileSaving) {
        this._showProfileSavingLoader = true;
      }
      this._profileSavingLoaderTimeout = null;
    }, 1000);
    this._myProfileChangeSubject.next({
      nickname: this._nickname,
      avatar_image_url: this._uploadImage,
    });
  }

  public _onClickUploadImage(): void {
    if (this._isProfileSaving) {
      return;
    }

    if (this._isImageUploading) {
      return;
    }

    this._fileInput.nativeElement.value = '';
    this._fileInput.nativeElement.click();
  }

  public _onClickDeleteImage(): void {
    if (this._isProfileSaving) {
      return;
    }

    if (this._isImageUploading) {
      return;
    }

    this._uploadImage = '';
    if (this._uploadImage !== this._baseAvatarImage) {
      this._isImageChanged = true;
    }
  }

  public async _onFileChanged(event: Event): Promise<void> {
    if (this._isProfileSaving) {
      return;
    }

    const inputElem = event.target as HTMLInputElement | null;
    const file = inputElem?.files?.[0];

    if (!file) {
      return;
    }

    this._isImageUploading = true;
    this._showImageUploadingLoader = false;
    this._clearImageUploadLoaderTimeout();
    this._imageUploadLoaderTimeout = setTimeout(() => {
      if (this._isImageUploading) {
        this._showImageUploadingLoader = true;
      }
      this._imageUploadLoaderTimeout = null;
    }, 1000);

    // 클라이언트에서 미리 이미지 압축
    try {
      const compressed = await this._imageCompressionService.compressImage(
        file,
        240
      );

      const normalizedFile =
        compressed instanceof File
          ? compressed
          : new File([compressed], file.name, {
              type: 'image/webp',
              lastModified: Date.now(),
            });

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(normalizedFile);
      this._imageUploadSubject.next(dataTransfer.files);
    } catch (error) {
      this._clearImageUploadLoaderTimeout();
      this._showImageUploadingLoader = false;
      this._isImageUploading = false;
      console.error(error);
      this._toastService.openWarning(
        '이미지 처리 중 오류가 발생했습니다. 다시 시도해주세요.'
      );
    }
  }
}
